import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { submitSurvey, type SurveySource } from "../api/me";
import { getPartners } from "../api/partners";

import { t } from "../lib/i18n";

export const SURVEY_SOURCE_OPTIONS: Array<{ label: string; value: SurveySource }> = [
  { label: "Shopify App Store で見つけた", value: "app_store" },
  { label: "Google などの検索", value: "search" },
  { label: "ブログ・記事・ニュース・SNS", value: "blog" },
  { label: "知人・取引先・パートナーの紹介", value: "referral" },
  { label: "その他", value: "other" },
];

// 紹介元パートナーのセレクトボックスを出す source（＝紹介系）。アフィブロガーは
// merchant が "blog"（ブログ・記事・ニュース・SNS）を選びがちで、referral 限定だと
// その帰属を取りこぼすため blog も対象にする。backend の REFERRAL_SOURCES と 1:1。
const PARTNER_SOURCES: ReadonlySet<SurveySource> = new Set(["blog", "referral"]);

export interface UseSurveyFormOptions {
  /** 紹介元候補（GET /api/partners）を取りに行くか。表示直前だけ true にする。 */
  enabled: boolean;
  /** 送信成功後に呼ばれる（["me"] の invalidate 後）。 */
  onAnswered?: () => void;
}

/**
 * 初回起動アンケートの回答状態・送信ミューテーションを一元管理する。
 *
 * インストール時の非ブロッキング表示（InstallSurveyModal）と、課金直前の
 * 飛ばせない関門（BillingSurveyGateModal）の両方がこのフックを共有する。
 */
export function useSurveyForm({ enabled, onAnswered }: UseSurveyFormOptions) {
  // 紹介元候補はモーダル表示時だけ取得する（非表示時は無駄打ちしない）。
  const { data: partnersData } = useQuery({
    queryKey: ["partners"],
    queryFn: getPartners,
    enabled,
  });
  const queryClient = useQueryClient();
  const [source, setSource] = useState<SurveySource | null>(null);
  const [partner, setPartner] = useState("");
  const [otherReferrer, setOtherReferrer] = useState("");

  const showsPartnerPicker = source !== null && PARTNER_SOURCES.has(source);
  const showsOtherSourceInput = source === "other";
  const isOtherPartner = false;

  const configuredOptions =
    source === "blog" || source === "referral"
      ? partnersData?.referrer_options?.[source]
      : undefined;
  // 先頭の「選択してください」以外は、オーナー管理画面の登録内容がSSOT。
  // 旧backendとの互換として referral だけ partners をフォールバックに使う。
  const partnerOptions = [
    { label: t("選択してください"), value: "" },
    ...(configuredOptions ??
      (source === "referral"
        ? (partnersData?.partners ?? []).map((p) => ({
            label: p.display_name,
            value: p.ref_code,
          }))
        : [])),
  ];

  const referrer = showsPartnerPicker
    ? partner || null
    : showsOtherSourceInput
      ? otherReferrer.trim() || null
      : null;

  const mutation = useMutation({
    mutationFn: () => submitSurvey({ source: source as SurveySource, referrer }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["me"] });
      onAnswered?.();
    },
  });

  // 送信可否: source 未選択、または紹介系で紹介元パートナー未選択なら不可。
  const canSubmit =
    source !== null &&
    !(showsPartnerPicker && partner === "") &&
    !(showsOtherSourceInput && otherReferrer.trim() === "");

  return {
    source,
    setSource,
    partner,
    setPartner,
    otherReferrer,
    setOtherReferrer,
    showsPartnerPicker,
    showsOtherSourceInput,
    isOtherPartner,
    partnerOptions,
    canSubmit,
    isPending: mutation.isPending,
    isError: mutation.isError,
    submit: () => mutation.mutate(),
  };
}
